import { Television } from "@phosphor-icons/react"

import { MsIconBtn } from "../ms"
import { paper } from "../theme"
import { CAST_ENABLED } from "./castConfig"
import { useCastSender } from "./useCastSender"
import { usePresentationSender } from "./usePresentationSender"

/**
 * "Cast to TV" for the lobby's share row.
 *
 * Two transports sit behind this one button, and the choice is not a preference
 * so much as an ordering: the **Presentation API** first, because a presentation
 * survives the phone that started it — lock the screen, refresh the lobby, lose
 * the tab, and the television keeps playing, where ending a Cast session takes
 * the board down with it. The **Cast SDK** is the fallback for Chromium builds
 * that don't offer the standard, or where no display answers it.
 *
 * Both talk to the same receiver page, so which one carried the room is
 * invisible from the television's side.
 *
 * Renders nothing at all when neither can work — no receiver id in the build, no
 * Cast API and no Presentation API (every iPhone), or no device on the network.
 * A disabled button would only raise the question it can't answer, and the room
 * already offers its QR and URL for getting a screen in on its own.
 */
export function CastButton({ roomCode }: { roomCode: string }) {
    const presentation = usePresentationSender(roomCode)
    const cast = useCastSender(roomCode)

    /*
     * Which transport, decided once.
     *
     * A live session always wins — whichever one is actually showing the board
     * is the one the button must be able to stop.
     *
     * Otherwise picture quality decides. A registered Cast receiver is loaded by
     * the television itself, so the board renders at full resolution on the
     * device. The Presentation API's https URL, on hardware that won't load it
     * natively, is served by rendering the page here and mirroring it as video —
     * which measurably looks soft and lags. So where an app id exists, Cast
     * leads; without one it's the only transport that can work at all.
     */
    const live = presentation.state.kind === "connected" ? presentation
        : cast.state.kind === "connected" ? cast
        : null

    const preferred = CAST_ENABLED ? cast : presentation
    const fallback = CAST_ENABLED ? presentation : cast
    const transport = live
        ?? (preferred.state.kind !== "unavailable" ? preferred : fallback)
    const { state, startCasting, stopCasting } = transport

    if (state.kind === "unavailable") return null

    if (state.kind === "connected") {
        return (
            <MsIconBtn
                haptic="select"
                onClick={stopCasting}
                title={state.deviceName ? `Casting to ${state.deviceName}` : "Casting"}
            >
                <Television size={16} weight="fill" color={paper.red} />
                {state.deviceName ? `On ${state.deviceName}` : "Stop casting"}
            </MsIconBtn>
        )
    }

    return (
        <MsIconBtn haptic="select" onClick={startCasting} disabled={state.kind === "connecting"}>
            <Television size={16} weight="bold" />
            {state.kind === "connecting" ? "Connecting…" : "Cast to TV"}
        </MsIconBtn>
    )
}
